import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, Car, ChevronRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const Configurator = () => {
  const navigate = useNavigate();
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [series, setSeries] = useState('');
  const [category, setCategory] = useState('');

  useEffect(() => {
    fetch(`${API}/api/shop/konfigurator/tree`)
      .then(r => r.json())
      .then(d => { setTree(d.brands || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const selectedBrand = useMemo(() => tree.find(b => b.alias === brand), [tree, brand]);
  const models = selectedBrand?.models || [];
  const selectedModel = useMemo(() => models.find(m => m.alias === model), [models, model]);
  const children = selectedModel?.children || [];

  const hasSeries = children.some(c => c.children && c.children.length > 0);
  const seriesList = hasSeries ? children : [];
  const selectedSeries = useMemo(() => seriesList.find(s => s.alias === series), [seriesList, series]);
  const categoryList = hasSeries ? (selectedSeries?.children || []) : children;

  const canGo = brand && model;

  const handleGo = () => {
    let catPath = brand;
    if (model) catPath += `/${model}`;
    if (hasSeries && series) catPath += `/${series}`;
    if (category) catPath += `/${category}`;
    navigate(`/shop?category=${encodeURIComponent(catPath)}`);
  };

  return (
    <section id="configurator" className="section-lg spotlight" data-testid="configurator-section">
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 1.5rem' }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ textAlign: 'center', marginBottom: '3rem' }}
        >
          <div className="caption" style={{ marginBottom: '1rem' }}>
            <Car size={12} style={{ display: 'inline', verticalAlign: 'middle' }} /> Fahrzeug-Konfigurator
          </div>
          <h2 className="heading-section" style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)' }}>
            Finde dein <span style={{ color: 'var(--primary)' }}>perfektes</span> Setup
          </h2>
          <p style={{ color: 'var(--fg-muted)', marginTop: '1rem', maxWidth: '500px', margin: '1rem auto 0' }}>
            Wähle dein Fahrzeug und finde passende Tuningteile direkt im Shop.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass"
          style={{ maxWidth: '900px', margin: '0 auto', padding: '2rem', borderRadius: '4px', background: 'rgba(8, 8, 8, 0.85)', backdropFilter: 'blur(20px)' }}
        >
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--fg-muted)' }}>
              Lade Fahrzeugdaten...
            </div>
          ) : (
            <>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '1rem',
                marginBottom: '1.5rem',
              }}>
                <div>
                  <label style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.15em', color: 'var(--primary)', marginBottom: '0.5rem', display: 'block', fontWeight: 600 }}>
                    01 &mdash; Marke
                  </label>
                  <select className="konfig-select" data-testid="konfig-brand" value={brand}
                    onChange={e => { setBrand(e.target.value); setModel(''); setSeries(''); setCategory(''); }}>
                    <option value="">Marke wählen...</option>
                    {tree.map(b => <option key={b.alias} value={b.alias}>{b.name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.15em', color: brand ? 'var(--primary)' : 'var(--fg-muted)', marginBottom: '0.5rem', display: 'block', fontWeight: 600 }}>
                    02 &mdash; Modell
                  </label>
                  <select className="konfig-select" data-testid="konfig-model" value={model} disabled={!brand}
                    onChange={e => { setModel(e.target.value); setSeries(''); setCategory(''); }}>
                    <option value="">{brand ? 'Modell wählen...' : 'Erst Marke wählen'}</option>
                    {models.map(m => <option key={m.alias} value={m.alias}>{m.name}</option>)}
                  </select>
                </div>
                {hasSeries && (
                  <div>
                    <label style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.15em', color: model ? 'var(--primary)' : 'var(--fg-muted)', marginBottom: '0.5rem', display: 'block', fontWeight: 600 }}>
                      03 &mdash; Baureihe
                    </label>
                    <select className="konfig-select" data-testid="konfig-series" value={series} disabled={!model}
                      onChange={e => { setSeries(e.target.value); setCategory(''); }}>
                      <option value="">Baureihe wählen...</option>
                      {seriesList.map(s => <option key={s.alias} value={s.alias}>{s.name}</option>)}
                    </select>
                  </div>
                )}
                {categoryList.length > 0 && (
                  <div>
                    <label style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.15em', color: (hasSeries ? series : model) ? 'var(--primary)' : 'var(--fg-muted)', marginBottom: '0.5rem', display: 'block', fontWeight: 600 }}>
                      {hasSeries ? '04' : '03'} &mdash; Kategorie
                    </label>
                    <select className="konfig-select" data-testid="konfig-category" value={category}
                      disabled={hasSeries ? !series : !model}
                      onChange={e => setCategory(e.target.value)}>
                      <option value="">Kategorie wählen...</option>
                      {categoryList.map(c => <option key={c.alias} value={c.alias}>{c.name}</option>)}
                    </select>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <button className="btn-primary" data-testid="konfig-go-btn" disabled={!canGo} onClick={handleGo}
                  style={{ opacity: canGo ? 1 : 0.4, cursor: canGo ? 'pointer' : 'not-allowed' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Search size={16} /> Zur Kategorie im Shop <ChevronRight size={16} />
                  </span>
                </button>
              </div>
            </>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default Configurator;
